import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building,
  User,
  Mail,
  Phone,
  Globe,
  Upload,
  Calendar,
  DollarSign,
  Users,
  MessageSquare,
  MessageCircle,
  Send,
  CheckCircle,
  Clock,
  FileText,
  ChevronRight,
  LogOut,
  Linkedin,
  Twitter,
  Facebook,
  Image as ImageIcon,
  Plus,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SponsorDashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [sponsor, setSponsor] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  
  // Dashboard data
  const [availableCourses, setAvailableCourses] = useState([]);
  const [myCommitments, setMyCommitments] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [messages, setMessages] = useState({ inbox: [], sent: [] });
  const [forumPosts, setForumPosts] = useState([]);
  
  // Dialogs
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [commitAmount, setCommitAmount] = useState('');
  const [commitNotes, setCommitNotes] = useState('');
  
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  
  const [showRegistrantsDialog, setShowRegistrantsDialog] = useState(false);
  const [registrants, setRegistrants] = useState([]);
  const [registrantsCourse, setRegistrantsCourse] = useState(null);
  
  const [showPostDetailDialog, setShowPostDetailDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    website: '',
    description: '',
    tagline: '',
    social_linkedin: '',
    social_twitter: '',
    social_facebook: ''
  });

  useEffect(() => {
    const token = searchParams.get('token');
    const savedSession = localStorage.getItem('sponsor_session_token');
    
    if (token) {
      verifyMagicLink(token);
    } else if (savedSession) {
      verifySession(savedSession);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/sponsor-auth/verify/${token}`);
      setSponsor(response.data.sponsor);
      setSessionToken(response.data.session_token);
      localStorage.setItem('sponsor_session_token', response.data.session_token);
      setAuthenticated(true);
      initializeProfileForm(response.data.sponsor);
      // Remove token from URL
      navigate('/sponsor-dashboard', { replace: true });
      toast.success('Welcome to your Sponsor Dashboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const verifySession = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/sponsor-auth/session/${token}`);
      setSponsor(response.data);
      setSessionToken(token);
      setAuthenticated(true);
      initializeProfileForm(response.data);
    } catch (error) {
      localStorage.removeItem('sponsor_session_token');
    } finally {
      setLoading(false);
    }
  };

  const initializeProfileForm = (sponsorData) => {
    setProfileForm({
      company_name: sponsorData.company_name || '',
      contact_name: sponsorData.contact_name || '',
      phone: sponsorData.phone || '',
      website: sponsorData.website || '',
      description: sponsorData.description || '',
      tagline: sponsorData.tagline || '',
      social_linkedin: sponsorData.social_linkedin || '',
      social_twitter: sponsorData.social_twitter || '',
      social_facebook: sponsorData.social_facebook || ''
    });
  };

  useEffect(() => {
    if (authenticated && sessionToken) {
      fetchDashboardData();
    }
  }, [authenticated, sessionToken]);

  const fetchDashboardData = async () => {
    try {
      const [coursesRes, commitmentsRes, directoryRes, messagesRes, forumRes] = await Promise.all([
        axios.get(`${API_URL}/sponsor-dashboard/available-courses?token=${sessionToken}`),
        axios.get(`${API_URL}/sponsor-dashboard/my-commitments?token=${sessionToken}`),
        axios.get(`${API_URL}/sponsor-dashboard/directory?token=${sessionToken}`),
        axios.get(`${API_URL}/sponsor-dashboard/messages?token=${sessionToken}`),
        axios.get(`${API_URL}/sponsor-dashboard/forum?token=${sessionToken}`)
      ]);
      
      setAvailableCourses(coursesRes.data);
      setMyCommitments(commitmentsRes.data);
      setDirectory(directoryRes.data);
      setMessages(messagesRes.data);
      setForumPosts(forumRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('sponsor_session_token');
    setAuthenticated(false);
    setSponsor(null);
    setSessionToken(null);
    navigate('/public/sponsors');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${API_URL}/sponsor-dashboard/profile?token=${sessionToken}`,
        profileForm
      );
      setSponsor(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/sponsor-dashboard/logo?token=${sessionToken}`,
        formData
      );
      setSponsor({ ...sponsor, logo_url: response.data.logo_url });
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleCommit = async () => {
    if (!commitAmount || parseFloat(commitAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/sponsor-dashboard/commit?token=${sessionToken}`, {
        sponsor_id: sponsor.id,
        scheduled_course_id: selectedCourse.id,
        amount: parseFloat(commitAmount),
        notes: commitNotes
      });
      
      toast.success('Commitment made successfully!');
      setShowCommitDialog(false);
      setCommitAmount('');
      setCommitNotes('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to commit');
    }
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/sponsor-dashboard/messages?token=${sessionToken}`, {
        sender_id: sponsor.id,
        recipient_id: messageRecipient.id,
        content: messageContent
      });
      
      toast.success('Message sent!');
      setShowMessageDialog(false);
      setMessageContent('');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleCreatePost = async () => {
    if (!postTitle.trim() || !postContent.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/sponsor-dashboard/forum?token=${sessionToken}`, {
        sponsor_id: sponsor.id,
        title: postTitle,
        content: postContent
      });
      
      toast.success('Post created!');
      setShowPostDialog(false);
      setPostTitle('');
      setPostContent('');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const viewRegistrants = async (commitment) => {
    try {
      const response = await axios.get(
        `${API_URL}/sponsor-dashboard/registrants/${commitment.scheduled_course_id}?token=${sessionToken}`
      );
      setRegistrants(response.data);
      setRegistrantsCourse(commitment.course);
      setShowRegistrantsDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load registrants');
    }
  };

  const viewPostDetail = async (post) => {
    try {
      const response = await axios.get(
        `${API_URL}/sponsor-dashboard/forum/${post.id}?token=${sessionToken}`
      );
      setSelectedPost(response.data);
      setShowPostDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load post');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/sponsor-dashboard/forum/${selectedPost.id}/comments?token=${sessionToken}`,
        {
          sponsor_id: sponsor.id,
          post_id: selectedPost.id,
          content: newComment
        }
      );
      
      setNewComment('');
      viewPostDetail(selectedPost); // Refresh
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 text-center">
            <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Sponsor Dashboard
            </h1>
            <p className="text-slate-600 mb-6">
              Please use the magic link sent to your email to access your dashboard.
            </p>
            <Button 
              onClick={() => navigate('/public/sponsors')}
              className="bg-sky-500 hover:bg-sky-600"
            >
              Apply to Become a Sponsor
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#1E3A5F] text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {sponsor?.logo_url ? (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                alt={sponsor.company_name}
                className="h-10 w-10 rounded object-contain bg-white p-1"
              />
            ) : (
              <div className="h-10 w-10 rounded bg-sky-600 flex items-center justify-center">
                <Building className="w-5 h-5" />
              </div>
            )}
            <div>
              <h1 className="font-bold">{sponsor?.company_name}</h1>
              <p className="text-xs text-sky-300">Sponsor Dashboard</p>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Completion Alert */}
        {!sponsor?.profile_completed && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Complete Your Profile</p>
                  <p className="text-sm text-amber-700">Upload your logo and complete your profile to get the most out of your sponsorship.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="courses" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Available Courses
            </TabsTrigger>
            <TabsTrigger value="commitments" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              My Commitments
              {myCommitments.length > 0 && (
                <Badge variant="secondary" className="ml-1">{myCommitments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="directory" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Sponsor Directory
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
              {messages.inbox.filter(m => !m.read).length > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {messages.inbox.filter(m => !m.read).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="forum" className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Forum
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Available Courses */}
          <TabsContent value="courses">
            <div className="grid gap-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Available Courses for Sponsorship
                </h2>
                <p className="text-sm text-slate-500">Target: $125-$150 per sponsor</p>
              </div>
              
              {availableCourses.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No upcoming courses available for sponsorship</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {availableCourses.map(course => (
                    <Card key={course.id} className="border-slate-200">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-semibold text-[#1E3A5F]">{course.course_title}</h3>
                            <p className="text-sm text-slate-500">
                              {new Date(course.scheduled_date).toLocaleDateString('en-US', {
                                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                              })} at {course.scheduled_time}
                            </p>
                          </div>
                          <Badge variant={course.delivery_format === 'webinar' ? 'default' : 'secondary'}>
                            {course.delivery_format}
                          </Badge>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-600">Sponsorship Progress</span>
                            <span className="font-medium">${course.total_committed} / ${course.sponsor_goal}</span>
                          </div>
                          <Progress value={(course.total_committed / course.sponsor_goal) * 100} className="h-2" />
                          <p className="text-xs text-slate-500 mt-1">
                            {course.num_sponsors} sponsor(s) committed • {course.registrant_count} registered
                          </p>
                        </div>
                        
                        {course.my_commitment ? (
                          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="flex items-center gap-2 text-emerald-700">
                              <CheckCircle className="w-4 h-4" />
                              <span className="font-medium">You committed ${course.my_commitment.amount}</span>
                            </div>
                          </div>
                        ) : (
                          <Button 
                            className="w-full bg-sky-500 hover:bg-sky-600"
                            onClick={() => {
                              setSelectedCourse(course);
                              setShowCommitDialog(true);
                            }}
                          >
                            <DollarSign className="w-4 h-4 mr-2" />
                            Commit to Sponsor
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* My Commitments */}
          <TabsContent value="commitments">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                My Sponsorship Commitments
              </h2>
              
              {myCommitments.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>You haven't committed to sponsor any courses yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {myCommitments.map(commitment => (
                    <Card key={commitment.id}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-[#1E3A5F]">
                              {commitment.course?.course_title}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {commitment.course && new Date(commitment.course.scheduled_date).toLocaleDateString('en-US', {
                                weekday: 'long', month: 'long', day: 'numeric'
                              })}
                            </p>
                            <p className="text-lg font-bold text-emerald-600 mt-2">
                              ${commitment.amount} committed
                            </p>
                            {commitment.notes && (
                              <p className="text-sm text-slate-500 mt-1">"{commitment.notes}"</p>
                            )}
                          </div>
                          <Button 
                            variant="outline"
                            onClick={() => viewRegistrants(commitment)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Registrants
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sponsor Directory */}
          <TabsContent value="directory">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sponsor Directory
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {directory.filter(s => s.id !== sponsor?.id).map(s => (
                  <Card key={s.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        {s.logo_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`}
                            alt={s.company_name}
                            className="w-16 h-16 object-contain rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center">
                            <Building className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#1E3A5F]">{s.company_name}</h3>
                          <p className="text-sm text-slate-500">{s.contact_name}</p>
                          {s.tagline && (
                            <p className="text-xs text-slate-400 mt-1">{s.tagline}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setMessageRecipient(s);
                            setShowMessageDialog(true);
                          }}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Message
                        </Button>
                        {s.website && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => window.open(s.website, '_blank')}
                          >
                            <Globe className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Messages
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Inbox */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Inbox
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {messages.inbox.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No messages yet</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.inbox.map(msg => (
                          <div 
                            key={msg.id} 
                            className={`p-3 rounded-lg border ${msg.read ? 'bg-white' : 'bg-sky-50 border-sky-200'}`}
                          >
                            <div className="flex justify-between items-start">
                              <p className="font-medium text-sm">{msg.sender_name}</p>
                              <span className="text-xs text-slate-400">
                                {new Date(msg.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Sent */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Sent
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {messages.sent.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No sent messages</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.sent.map(msg => (
                          <div key={msg.id} className="p-3 rounded-lg border bg-white">
                            <div className="flex justify-between items-start">
                              <p className="font-medium text-sm">To: {msg.recipient_name}</p>
                              <span className="text-xs text-slate-400">
                                {new Date(msg.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Forum */}
          <TabsContent value="forum">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Sponsor Forum
                </h2>
                <Button onClick={() => setShowPostDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </div>
              
              {forumPosts.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No forum posts yet. Be the first to start a discussion!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {forumPosts.map(post => (
                    <Card key={post.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewPostDetail(post)}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          {post.author?.logo_url ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${post.author.logo_url}`}
                              alt=""
                              className="w-10 h-10 rounded object-contain"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                              <Building className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="font-semibold text-[#1E3A5F]">{post.title}</h3>
                            <p className="text-sm text-slate-500">
                              by {post.author?.company_name} • {new Date(post.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{post.content}</p>
                            <div className="flex items-center gap-2 mt-3 text-sm text-slate-400">
                              <MessageCircle className="w-4 h-4" />
                              {post.comment_count} comments
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile">
            <div className="max-w-2xl">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Profile
              </h2>
              
              <Card>
                <CardContent className="pt-6">
                  {/* Logo Upload */}
                  <div className="flex items-center gap-6 mb-6 pb-6 border-b">
                    {sponsor?.logo_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                        alt="Logo"
                        className="w-24 h-24 object-contain rounded-lg border"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-10 h-10 text-slate-300" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium mb-2">Company Logo</h3>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                        />
                        <div className="inline-flex items-center px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          {sponsor?.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Company Name</Label>
                        <Input
                          value={profileForm.company_name}
                          onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Contact Name</Label>
                        <Input
                          value={profileForm.contact_name}
                          onChange={(e) => setProfileForm({ ...profileForm, contact_name: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input
                          value={profileForm.website}
                          onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Tagline</Label>
                      <Input
                        placeholder="A short tagline for your company"
                        value={profileForm.tagline}
                        onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        rows={4}
                        value={profileForm.description}
                        onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                      />
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3">Social Links</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Linkedin className="w-5 h-5 text-blue-600" />
                          <Input
                            placeholder="LinkedIn URL"
                            value={profileForm.social_linkedin}
                            onChange={(e) => setProfileForm({ ...profileForm, social_linkedin: e.target.value })}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <Twitter className="w-5 h-5 text-sky-500" />
                          <Input
                            placeholder="Twitter URL"
                            value={profileForm.social_twitter}
                            onChange={(e) => setProfileForm({ ...profileForm, social_twitter: e.target.value })}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <Facebook className="w-5 h-5 text-blue-700" />
                          <Input
                            placeholder="Facebook URL"
                            value={profileForm.social_facebook}
                            onChange={(e) => setProfileForm({ ...profileForm, social_facebook: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full bg-[#1E3A5F] hover:bg-[#162d4a]">
                      Save Profile
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Commit Dialog */}
      <Dialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit to Sponsor</DialogTitle>
          </DialogHeader>
          {selectedCourse && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedCourse.course_title}</p>
                <p className="text-sm text-slate-500">
                  {new Date(selectedCourse.scheduled_date).toLocaleDateString()}
                </p>
              </div>
              
              <div>
                <Label>Commitment Amount ($)</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="125"
                  value={commitAmount}
                  onChange={(e) => setCommitAmount(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Suggested: $125 - $150 per sponsor
                </p>
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Any notes about your commitment..."
                  value={commitNotes}
                  onChange={(e) => setCommitNotes(e.target.value)}
                />
              </div>
              
              <Button onClick={handleCommit} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Confirm Commitment
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
          </DialogHeader>
          {messageRecipient && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                To: <strong>{messageRecipient.company_name}</strong>
              </p>
              <Textarea
                rows={4}
                placeholder="Write your message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
              />
              <Button onClick={handleSendMessage} className="w-full">
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Post Dialog */}
      <Dialog open={showPostDialog} onOpenChange={setShowPostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Forum Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                placeholder="Post title"
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                rows={5}
                placeholder="Write your post..."
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
              />
            </div>
            <Button onClick={handleCreatePost} className="w-full">
              Create Post
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registrants Dialog */}
      <Dialog open={showRegistrantsDialog} onOpenChange={setShowRegistrantsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Course Registrants</DialogTitle>
          </DialogHeader>
          {registrantsCourse && (
            <div>
              <p className="text-sm text-slate-600 mb-4">
                {registrantsCourse.course_title} - {registrants.length} registrants
              </p>
              {registrants.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No registrants yet</p>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {registrants.map((reg, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium">{reg.first_name} {reg.last_name}</p>
                      <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mt-1">
                        <span>{reg.company || 'N/A'}</span>
                        <span>{reg.email}</span>
                        <span>{reg.phone || 'N/A'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post Detail Dialog */}
      <Dialog open={showPostDetailDialog} onOpenChange={setShowPostDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedPost && (
            <div>
              <div className="flex items-start gap-4 mb-4">
                {selectedPost.author?.logo_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedPost.author.logo_url}`}
                    alt=""
                    className="w-12 h-12 rounded object-contain"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                    <Building className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg">{selectedPost.title}</h3>
                  <p className="text-sm text-slate-500">
                    by {selectedPost.author?.company_name} • {new Date(selectedPost.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <p className="text-slate-700 whitespace-pre-wrap mb-6">{selectedPost.content}</p>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Comments ({selectedPost.comments?.length || 0})</h4>
                
                <div className="space-y-3 mb-4">
                  {selectedPost.comments?.map(comment => (
                    <div key={comment.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.author?.company_name}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{comment.content}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <Button onClick={handleAddComment}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
